import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Loader2 } from "lucide-react";
import { Reveal } from "../common/Reveal";
import { Img } from "../common/Img";
import { ArticleModal } from "../modals/Modals";
import { articleService, resolveImageUrl } from "../../services/catalogService";
import { useLanguage } from "../../context/LanguageContext";
import { fadeUp, viewport, stagger } from "../../lib/animations";

// Task 12 — blog articles are real content from MongoDB (admin-managed).
export default function FloralNews() {
  const { t, localize } = useLanguage();
  const [articles, setArticles] = useState(null);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    articleService.list().then(setArticles).catch(() => setArticles([]));
  }, []);

  return (
    <section id="news" className="bg-[#F6F2EC] py-24 md:py-32" data-testid="news-section">
      <div className="container-tessa">
        <div className="mb-14 flex flex-col justify-between gap-6 md:flex-row md:items-end">
          <Reveal>
            <span className="font-sans text-xs uppercase tracking-[0.24em] text-[#D92A47]">
              {t("news.eyebrow")}
            </span>
            <h2 className="mt-4 font-serif text-4xl leading-[1.0] text-[#183C32] md:text-5xl lg:text-6xl">
              {t("news.title1")}
              <br />
              <span className="italic text-[#D92A47]">{t("news.title2")}</span>
            </h2>
          </Reveal>
          <Link
            to="/blog"
            data-testid="news-view-all"
            className="self-start border-b border-[#D92A47] pb-1 text-[12px] font-semibold uppercase tracking-[0.16em] text-[#252525] transition-colors hover:text-[#D92A47] md:self-end"
          >
            {t("news.viewAll")}
          </Link>
        </div>

        {articles === null ? (
          <div className="text-[#817B74] py-16 text-center">
            <Loader2 className="animate-spin inline mr-2" size={16} />Loading…
          </div>
        ) : articles.length === 0 ? null : (
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={viewport}
            variants={stagger(0.12)}
            className="grid grid-cols-1 gap-8 md:grid-cols-3 md:gap-6"
          >
            {articles.slice(0, 3).map((article) => (
              <motion.button
                key={article.id}
                variants={fadeUp}
                onClick={() => setSelected(article)}
                data-testid={`article-${article.id}`}
                className="group text-left"
              >
                <div className="relative aspect-[4/3] overflow-hidden bg-[#EDE8E0]">
                  {article.image && (
                    <Img
                      src={resolveImageUrl(article.image)}
                      alt={localize(article, "title")}
                      className="h-full w-full object-cover transition-transform duration-[900ms] ease-out group-hover:scale-105"
                    />
                  )}
                </div>
                <div className="mt-5 flex items-center gap-3 text-[11px] uppercase tracking-[0.16em] text-[#D92A47]">
                  <span>{localize(article, "category")}</span>
                  <span className="text-[#817B74]">· {article.date}</span>
                </div>
                <h3 className="mt-3 font-serif text-2xl leading-tight text-[#252525] transition-colors group-hover:text-[#D92A47]">
                  {localize(article, "title")}
                </h3>
                <p className="mt-2 text-[15px] leading-relaxed text-[#817B74]">
                  {localize(article, "excerpt")}
                </p>
                <span className="mt-4 inline-flex items-center gap-2 text-[12px] font-semibold uppercase tracking-[0.14em] text-[#252525]">
                  {t("news.viewArticle")}
                  <ArrowRight
                    size={15}
                    className="transition-transform group-hover:translate-x-1"
                  />
                </span>
              </motion.button>
            ))}
          </motion.div>
        )}
      </div>

      <ArticleModal article={selected} open={Boolean(selected)} onClose={() => setSelected(null)} />
    </section>
  );
}
